// useRevelar.js — Estado de aparição de um elemento, à prova de falha.
//
// Devolve um dos três estados:
//   "oculto"    → ainda não entrou na tela; fica transparente
//   "revelando" → entrou; a transição de entrada está rodando
//   "pronto"    → passou o tempo da transição; SEM classe de animação nenhuma
//
// POR QUE EXISTE O ESTADO "pronto" — é o ponto todo deste arquivo.
// Um reveal comum parte de `opacity: 0` e só chega a 1 quando a animação roda.
// Isso amarra a EXISTÊNCIA do conteúdo ao relógio de animação do navegador: se
// ele não andar, o conteúdo não aparece. Numa vitrine, animação perdida é um
// detalhe; catálogo em branco é a loja fechada.
//
// O "pronto" corta essa amarra. Um `setTimeout` — que usa o relógio de
// TEMPORIZADORES, independente do de animação — tira as classes de animação
// depois da duração da entrada. Se a animação rodou, remover as classes não
// muda nada (o elemento já está no estado final). Se não rodou, ele salta para
// visível. O pior caso vira "apareceu sem efeito", nunca "não apareceu".
//
// Pelo mesmo motivo há duas conferências de geometria além do
// IntersectionObserver: nenhuma peça sozinha decide se o conteúdo existe.
//
// (Contexto histórico: esta blindagem nasceu de prints do catálogo que saíam
// com a grade vazia. A causa acabou sendo o `--virtual-time-budget` do Chrome
// headless, que congela o pipeline de renderização — com captura realista o
// site sempre renderizou certo. A blindagem ficou mesmo assim: ela custa um
// `setTimeout` e elimina uma classe inteira de falha.)
import { useEffect, useState } from "react";

/** Transição (0,5 s) + maior atraso em cascata (0,4 s) + folga. */
const TEMPO_ATE_PRONTO = 1200;

const useRevelar = (ref, amount = 0.15) => {
  const [estado, setEstado] = useState("oculto");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Navegador sem IntersectionObserver: mostra o conteúdo, sem animação.
    if (typeof IntersectionObserver === "undefined") {
      setEstado("pronto");
      return;
    }

    let revelado = false;
    let observador;
    let temporizadorGeometria;
    let temporizadorPronto;

    const conferirGeometria = () => {
      const r = el.getBoundingClientRect();
      const alturaTela = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < alturaTela && r.bottom > 0) revelar();
    };

    const pararDeObservar = () => {
      clearTimeout(temporizadorGeometria);
      if (observador) observador.disconnect();
      window.removeEventListener("scroll", conferirGeometria);
      window.removeEventListener("resize", conferirGeometria);
    };

    function revelar() {
      if (revelado) return;
      revelado = true;
      pararDeObservar();
      setEstado("revelando");
      temporizadorPronto = setTimeout(() => setEstado("pronto"), TEMPO_ATE_PRONTO);
    }

    observador = new IntersectionObserver(
      ([entrada]) => { if (entrada.isIntersecting) revelar(); },
      { threshold: Math.min(Math.max(amount, 0), 1) }
    );
    observador.observe(el);

    temporizadorGeometria = setTimeout(conferirGeometria, 700);
    window.addEventListener("scroll", conferirGeometria, { passive: true });
    window.addEventListener("resize", conferirGeometria, { passive: true });

    return () => {
      pararDeObservar();
      clearTimeout(temporizadorPronto);
    };
  }, [ref, amount]);

  return estado;
};

/** Classes de animação para um dado estado (vazio quando "pronto"). */
export const classesDoEstado = (estado, extra = "") => {
  if (estado === "pronto") return "";
  return `revelar ${extra} ${estado === "revelando" ? "revelar-visivel" : ""}`;
};

export default useRevelar;
